import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  Button,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Slide,
  Toolbar,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import RefreshIcon from "@material-ui/icons/Refresh";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import SyncIcon from "@material-ui/icons/Sync";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import GetAppIcon from "@material-ui/icons/GetApp";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import moment from "moment";
import { i18n } from "../../translate/i18n";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import Alert from "@material-ui/lab/Alert";
import FileManagerSelectorModal from "../../components/FileManagerSelectorModal";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
  },
  appBar: {
    position: 'relative',
    backgroundColor: '#333'
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  setupArea: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    maxWidth: 600,
    margin: "0 auto",
    marginTop: theme.spacing(4)
  },
  boxCard: {
    marginBottom: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    transition: "0.3s",
    "&:hover": {
      boxShadow: "0 8px 16px rgba(0,0,0,0.3)"
    }
  },
  boxContent: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  textCols: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  imageCol: {
    width: 300,
    height: 300,
    backgroundColor: "#eee",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden"
  },
  previewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    cursor: "pointer"
  },
  overlay: {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "0.2s",
    "&:hover": {
      opacity: 1
    }
  },
  globalActions: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
}));


const getFileUrl = (path) => {
  if (!path || typeof path !== 'string') return "";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  
  if (path.startsWith("/")) return `${backendUrl}${path}`;
  
  if (path.startsWith("http")) {
    try {
      const urlObj = new URL(path);
      if (urlObj.pathname.startsWith('/public/')) {
        return `${backendUrl}${urlObj.pathname}`;
      }
    } catch (e) {}
    
    if (path.includes("localhost")) {
      return path.replace("http://localhost:8080", backendUrl).replace("http://localhost:3000", backendUrl);
    }
    
    return path;
  }
  
  return `${backendUrl}/${path}`;
};

const AiContentFactory = ({ open, onClose, selectedWhatsapp, availableGroups = [], onScheduled, onRefreshGrid, initialQuantity, editSchedule, type = "community",
  restrictedGroups = {},
  syncingGroups = false,
  onSyncGroups = () => {}
}) => {
  const classes = useStyles();
  const [numImages, setNumImages] = useState(initialQuantity || 1);
  const [boxes, setBoxes] = useState([]);
  const [modelCost, setModelCost] = useState(0.06);
  const [modelName, setModelName] = useState("AI Model");

  useEffect(() => {
    const fetchCost = async () => {
      try {
        const { data: companySetting } = await api.get("/companySettingOne", { params: { column: "aiModel" } });
        const activeModelId = companySetting?.aiModel || "gpt-image-2-text-to-image";
        const { data: models } = await api.get("/ai-models");
        const activeModel = models.find(m => m.apiIdentifier === activeModelId);
        if (activeModel) {
          if (activeModel.credits) setModelCost(Number(activeModel.credits));
          if (activeModel.name) setModelName(activeModel.name);
        }
      } catch (err) {
        console.error("Error fetching AI model cost:", err);
      }
    };
    if (open) fetchCost();
  }, [open]);
  
  // Si se nos pasa un `editSchedule`, inicializamos una única caja con sus datos.
  // Si no, no hacemos nada y dejamos que se muestre el area de setup.
  useEffect(() => {
    if (open) {
      if (editSchedule) {
        let parsedPrompt = editSchedule.aiPrompt || "";
        let parsedRef = null;
        let parsedGenImgs = null;
        
        if (parsedPrompt.includes("|||GEN_IMGS|||")) {
          const parts = parsedPrompt.split("|||GEN_IMGS|||");
          parsedPrompt = parts[0];
          try {
            parsedGenImgs = JSON.parse(parts[1]);
          } catch(e) {}
        }

        if (parsedPrompt.includes("|||REF_IMG|||")) {
          const parts = parsedPrompt.split("|||REF_IMG|||");
          parsedPrompt = parts[0];
          parsedRef = parts[1];
        }
        
        let genImages = [];
        if (parsedGenImgs && Array.isArray(parsedGenImgs)) {
          genImages = parsedGenImgs;
        } else if (editSchedule.mediaUrl) {
          genImages = [{
            imageUrl: `${process.env.REACT_APP_BACKEND_URL}${editSchedule.mediaUrl.startsWith('/public') ? '' : '/public'}${editSchedule.mediaUrl.startsWith('/company') ? editSchedule.mediaUrl : `/company${editSchedule.companyId || 1}${editSchedule.mediaUrl}`}`,
            rawMediaUrl: editSchedule.mediaUrl.split('/').pop(),
            mediaId: null,
            aspectRatio: "1:1"
          }];
        }

        let initialRatios = { "1:1": true, "9:16": false, "4:5": false };
        if (genImages && genImages.length > 0) {
          initialRatios = { "1:1": false, "9:16": false, "4:5": false };
          genImages.forEach(img => {
            if (img.aspectRatio) initialRatios[img.aspectRatio] = true;
          });
        }

        setNumImages(1);
        setBoxes([{
          id: editSchedule.id || 1,
          prompt: parsedPrompt,
          caption: editSchedule.caption || "",
          status: "generated", // Asumimos que ya está generada si se está editando
          generatedImages: genImages,
          referenceImage: parsedRef,
          scheduledAt: moment(editSchedule.scheduledAt).format("YYYY-MM-DDTHH:mm"),
          groupIds: editSchedule.groupIds || [],
          whatsappId: editSchedule.whatsappId,
          editId: editSchedule.id,
          aspectRatios: initialRatios,
          generationMethod: "native",
          selectedImageIndex: 0
        }]);
      } else {
        setNumImages(1);
        setBoxes([{
          id: 1,
          prompt: "",
          caption: "",
          status: "empty",
          generatedImages: [],
          referenceImage: null,
          scheduledAt: moment().add(5, 'minutes').format("YYYY-MM-DDTHH:mm"),
          groupIds: [],
          aspectRatios: { "1:1": true, "9:16": false, "4:5": false },
          generationMethod: "native",
          selectedImageIndex: 0
        }]);
      }
    }
  }, [open, editSchedule]);
  
  const [selectorBoxId, setSelectorBoxId] = useState(null);

  useEffect(() => {
    if (boxes.length > 0) {
      const serializableBoxes = boxes.map(b => ({ ...b, referenceImage: null }));
      localStorage.setItem("aiFactory_boxes", JSON.stringify(serializableBoxes));
    }
  }, [boxes]);
  
  // State for image zoom
  const [zoomImg, setZoomImg] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, boxId: null, isAll: false, cost: 0, count: 0 });


  const updateBox = (id, updates) => {
    setBoxes(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  
  const handleGenerate = (id) => {
    const box = boxes.find(b => b.id === id);
    if (!box.prompt) {
      toast.error("El prompt (Idea de Imagen) es obligatorio.");
      return;
    }
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de generar.");
      return;
    }
    const selectedRatios = Object.keys(box.aspectRatios).filter(k => box.aspectRatios[k]);
    const ratiosCount = selectedRatios.length > 0 ? selectedRatios.length : 1;
    const multiplier = box.generationMethod === "native" ? ratiosCount : 1;
    setConfirmDialog({ open: true, boxId: id, isAll: false, cost: modelCost * multiplier, count: ratiosCount });
  };

  const handleGenerateAll = () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de generar.");
      return;
    }
    const emptyBoxes = boxes.filter(b => b.status === "empty" && b.prompt);
    if (emptyBoxes.length === 0) {
      toast.error("No hay imágenes listas (con prompt) para generar.");
      return;
    }
    let totalCost = 0;
    let totalCount = 0;
    emptyBoxes.forEach(box => {
      const selectedRatios = Object.keys(box.aspectRatios).filter(k => box.aspectRatios[k]);
      const ratiosCount = selectedRatios.length > 0 ? selectedRatios.length : 1;
      const multiplier = box.generationMethod === "native" ? ratiosCount : 1;
      totalCost += modelCost * multiplier;
      totalCount += ratiosCount;
    });
    setConfirmDialog({ open: true, boxId: null, isAll: true, cost: totalCost, count: totalCount });
  };

  const confirmAndExecute = async () => {
    const { isAll, boxId } = confirmDialog;
    setConfirmDialog({ ...confirmDialog, open: false });
    if (isAll) {
      const emptyBoxes = boxes.filter(b => b.status === "empty" && b.prompt);
      for (const box of emptyBoxes) {
        await executeGenerate(box.id);
      }
    } else {
      await executeGenerate(boxId);
    }
  };

  const executeGenerate = async (id) => {
    const box = boxes.find(b => b.id === id);
    if (!box.prompt) {
      toast.error("El prompt (Idea de Imagen) es obligatorio.");
      return;
    }
    
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de generar.");
      return;
    }
    
    updateBox(id, { status: "generating" });
    try {
      const selectedRatios = Object.keys(box.aspectRatios).filter(k => box.aspectRatios[k]);
      if (selectedRatios.length === 0) selectedRatios.push("1:1"); // Fallback
      
      let data;
      if (box.referenceImage && typeof box.referenceImage !== 'string') {
        const formData = new FormData();
        formData.append("prompt", box.prompt);
        formData.append("file", box.referenceImage);
        formData.append("aspectRatios", JSON.stringify(selectedRatios));
        formData.append("generationMethod", box.generationMethod);
        const res = await api.post("/ai-media/generate", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        data = res.data;
      } else {
        const res = await api.post("/ai-media/generate", {
          prompt: box.prompt,
          mediaPath: box.referenceImage,
          aspectRatios: selectedRatios,
          generationMethod: box.generationMethod
        });
        data = res.data;
      }
      
      let referenceImage = data.referenceImage || box.referenceImage;
      const responseData = data.data || data;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const generatedArray = Array.isArray(responseData) ? responseData : [responseData];
      const newGeneratedImages = generatedArray.map(item => ({
        imageUrl: `${backendUrl}/public/company${item.companyId}/campaigns/${item.mediaUrl}`,
        rawMediaUrl: item.mediaUrl,
        mediaId: item.id,
        aspectRatio: item.aspectRatio || "1:1"
      }));

      // AUTO-CREATE CARD
      let editId = box.editId;
      try {
        let finalPrompt = box.prompt || "";
        if (referenceImage && typeof referenceImage === 'string') {
           finalPrompt += `|||REF_IMG|||${referenceImage}`;
        }
        if (newGeneratedImages && newGeneratedImages.length > 0) {
           finalPrompt += `|||GEN_IMGS|||${JSON.stringify(newGeneratedImages)}`;
        }
        
        const payload = type === "status" ? {
          scheduledAt: box.scheduledAt || new Date().toISOString(),
          mediaType: "image",
          mediaUrl: `/campaigns/${newGeneratedImages[0].rawMediaUrl}`,
          caption: box.caption || "",
          aiGeneration: true,
          aiPrompt: finalPrompt,
          isActive: false,
          whatsappId: selectedWhatsapp,
          repeatDaily: false
        } : {
          groupIds: box.groupIds || [],
          scheduledAt: box.scheduledAt || new Date().toISOString(),
          mediaType: "image",
          mediaUrl: `/campaigns/${newGeneratedImages[0].rawMediaUrl}`,
          caption: box.caption || "",
          aiGeneration: true,
          aiPrompt: finalPrompt,
          mentionAll: false,
          sendInterval: 15,
          isActive: false, // Save as inactive (draft)
          isCommunity: type === "community",
          whatsappId: selectedWhatsapp
        };
        
        const endpoint = type === "status" ? "/whatsapp-status-schedule" : "/group-schedule";

        if (editId) {
          await api.put(`${endpoint}/${editId}`, payload);
        } else {
          const createRes = await api.post(endpoint, payload);
          editId = createRes.data.id;
        }
        // Llamar a una función silenciosa para refrescar el grid de fondo si existe
        if (typeof onRefreshGrid === 'function') onRefreshGrid();
      } catch (err) {
        console.error("Error auto-creating schedule", err);
      }

      updateBox(id, { 
        status: "generated", 
        generatedImages: newGeneratedImages, 
        selectedImageIndex: 0,
        referenceImage: referenceImage,
        editId: editId
      });
      toast.success(`Imagen generada con éxito. Borrador guardado.`);
    } catch (err) {
      console.error(err);
      const backendError = err.response?.data?.error || err.message || "";
      if (backendError.includes("ERR_INSUFFICIENT_AI_CREDITS")) {
        toast.error(`Saldo insuficiente. Recarga tu Saldo IA para generar imágenes.`);
      } else if (backendError) {
        toast.error(`Error: ${backendError}`);
      } else {
        toast.error(`Error al generar imagen en la Imagen ${id}.`);
      }
      updateBox(id, { status: "empty" });
    }
  };

  const handleSaveDraft = async (id) => {
    const box = boxes.find(b => b.id === id);
    if (!box.prompt && !box.caption) {
      toast.error("Debes ingresar al menos un texto (Prompt o Copy) para guardar.");
      return;
    }
    
    try {
      let editId = box.editId;
      
      const selectedMedia = box.generatedImages && box.generatedImages.length > 0 
        ? box.generatedImages[box.selectedImageIndex || 0] 
        : null;

      let finalPrompt = box.prompt || "";
      if (box.referenceImage && typeof box.referenceImage === 'string') {
         finalPrompt += `|||REF_IMG|||${box.referenceImage}`;
      }
      if (box.generatedImages && box.generatedImages.length > 0) {
         finalPrompt += `|||GEN_IMGS|||${JSON.stringify(box.generatedImages)}`;
      }

      const actualMediaType = (selectedMedia && selectedMedia.rawMediaUrl && selectedMedia.rawMediaUrl.match(/\.(mp4|webm|mov)$/i)) ? "video" : "image";

      const payload = type === "status" ? {
        scheduledAt: box.scheduledAt || new Date().toISOString(),
        mediaType: actualMediaType,
        mediaUrl: selectedMedia ? `/campaigns/${selectedMedia.rawMediaUrl}` : "",
        caption: box.caption || "",
        aiGeneration: true,
        aiPrompt: finalPrompt,
        isActive: false,
        whatsappId: box.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp),
        repeatDaily: false
      } : {
        groupIds: box.groupIds || [],
        scheduledAt: box.scheduledAt || new Date().toISOString(),
        mediaType: actualMediaType,
        mediaUrl: selectedMedia ? `/campaigns/${selectedMedia.rawMediaUrl}` : "",
        caption: box.caption || "",
        aiGeneration: true,
        aiPrompt: finalPrompt,
        mentionAll: false,
        sendInterval: 15,
        isActive: false, // Save as inactive (draft)
        isCommunity: type === "community",
        whatsappId: box.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp)
      };
      
      if (!payload.whatsappId || payload.whatsappId === "all") {
        toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de guardar.");
        return;
      }

      const endpoint = type === "status" ? "/whatsapp-status-schedule" : "/group-schedule";

      if (editId) {
        await api.put(`${endpoint}/${editId}`, payload);
      } else {
        const createRes = await api.post(endpoint, payload);
        editId = createRes.data.id;
        updateBox(id, { editId: editId });
      }
      if (typeof onRefreshGrid === 'function') onRefreshGrid();
      
      toast.success(`Borrador guardado exitosamente.`);
    } catch (err) {
      console.error("Error saving draft", err);
      toast.error(`Error al guardar borrador.`);
    }
  };

  const executeGenerateAll = async () => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de generar.");
      return;
    }

    // Only generate boxes that are empty
    const pendingBoxes = boxes.filter(b => b.status === "empty");
    if (pendingBoxes.length === 0) {
      toast.error("No hay imágenes vacías para generar.");
      return;
    }

    // Run sequentially to avoid rate limits
    for (const box of pendingBoxes) {
      if (!box.prompt) {
        toast.warning(`La Imagen ${box.id} no tiene prompt. Se saltará.`);
        continue;
      }
      await executeGenerate(box.id);
    }
  };

  const handleSchedule = async (id) => {
    const box = boxes.find(b => b.id === id);
    if (type !== "status" && (!box.scheduledAt || box.groupIds.length === 0)) {
      toast.error("Debes seleccionar Fecha/Hora y al menos un destino.");
      return;
    }
    if (type === "status" && !box.scheduledAt) {
      toast.error("Debes seleccionar Fecha/Hora.");
      return;
    }

    try {
      const selectedMedia = box.generatedImages && box.generatedImages.length > 0 
        ? box.generatedImages[box.selectedImageIndex || 0] 
        : null;
        
      if (!selectedMedia) {
        toast.error("No hay imagen generada para programar.");
        return;
      }

      let finalPrompt = box.prompt || "";
      if (box.referenceImage && typeof box.referenceImage === 'string') {
         finalPrompt += `|||REF_IMG|||${box.referenceImage}`;
      }
      if (box.generatedImages && box.generatedImages.length > 0) {
         finalPrompt += `|||GEN_IMGS|||${JSON.stringify(box.generatedImages)}`;
      }

      const actualMediaType = (selectedMedia && selectedMedia.rawMediaUrl && selectedMedia.rawMediaUrl.match(/\.(mp4|webm|mov)$/i)) ? "video" : "image";

      const payload = type === "status" ? {
        scheduledAt: box.scheduledAt,
        mediaType: actualMediaType,
        mediaUrl: `/campaigns/${selectedMedia.rawMediaUrl}`,
        caption: box.caption,
        aiGeneration: true,
        aiPrompt: finalPrompt,
        isActive: true,
        whatsappId: box.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp),
        repeatDaily: false
      } : {
        groupIds: box.groupIds,
        scheduledAt: box.scheduledAt,
        mediaType: actualMediaType,
        mediaUrl: `/campaigns/${selectedMedia.rawMediaUrl}`,
        caption: box.caption,
        aiGeneration: true,
        aiPrompt: finalPrompt,
        mentionAll: false,
        sendInterval: 15,
        isActive: true,
        isCommunity: type === "community",
        whatsappId: box.whatsappId || (selectedWhatsapp === "all" ? "" : selectedWhatsapp)
      };

      if (!payload.whatsappId || payload.whatsappId === "all") {
        toast.error("Por favor selecciona una conexión de WhatsApp específica arriba antes de programar.");
        return;
      }

      const endpoint = type === "status" ? "/whatsapp-status-schedule" : "/group-schedule";

      if (box.editId) {
        await api.put(`${endpoint}/${box.editId}`, payload);
      } else {
        await api.post(endpoint, payload);
      }
      
      toast.success(`Imagen programada exitosamente.`);
      
      // Remover la caja ya que ahora vive en la lista principal
      setBoxes(prev => {
        const next = prev.filter(b => b.id !== id);
        if (next.length === 0) {
          onClose();
        }
        return next;
      });
      
      if (onScheduled) onScheduled();
    } catch (err) {
      console.error(err);
      toast.error(`Error al programar la imagen.`);
    }
  };


  const handleCreateBoxes = () => {
    const newBoxes = [];
    for (let i = 0; i < numImages; i++) {
      newBoxes.push({
        id: i + 1,
        prompt: "",
        caption: "",
        status: "empty",
        generatedImages: [],
        referenceImage: null,
        scheduledAt: "",
        groupIds: [],
        aspectRatios: { "1:1": true, "9:16": true, "4:5": true },
        generationMethod: "native",
        selectedImageIndex: 0
      });
    }
    setBoxes(newBoxes);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle disableTypography style={{ padding: '16px 24px', borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: "bold" }}>{i18n.t("statusScheduler.newAiPost")}</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: 0, backgroundColor: 'transparent' }}>
          <Box style={{ overflowY: "auto", flexGrow: 1 }}>
          <Box px={3} py={1}>
            <Box margin="0 auto">
            {boxes.map((box, index) => (
              <Card key={box.id} className={classes.boxCard} elevation={box.status === 'scheduled' ? 1 : 4} style={{ opacity: box.status === 'scheduled' ? 0.6 : 1, marginBottom: 8 }}>
                <CardContent style={{ padding: '16px 16px 0px 16px !important' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} width="100%">
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>{i18n.t("statusScheduler.newAiPost")} {box.status === 'scheduled' && "✅ (Programada)"}</Typography>
                      {box.status === 'scheduled' && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 600 }}>
                            📅 {new Date(box.scheduledAt).toLocaleString('es-PE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </Typography>
                          <Button size="small" variant="outlined" color="secondary" style={{ textTransform: 'none', fontSize: 11 }}
                            onClick={() => updateBox(box.id, { status: 'generated' })}>
                            ✏️ Editar
                          </Button>
                        </Box>
                      )}
                    </Box>

                    <Grid container spacing={3}>
                      {/* Columna Textos: Prompt */}
                      <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TextField
                          label={i18n.t("statusScheduler.aiPrompt")}
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={4}
                      fullWidth
                      disabled={box.status === "scheduled" || box.status === "generating"}
                      value={box.prompt}
                      onChange={e => updateBox(box.id, { prompt: e.target.value })}
                      inputProps={{ style: { paddingRight: '12px', textAlign: 'justify' } }}
                    />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ marginTop: 16, marginBottom: 8 }}>
                      <Box style={{ flexGrow: 1, marginRight: 16 }}>
                        <Alert icon={false} variant="outlined" style={{ borderRadius: 12, backgroundColor: 'rgba(255, 152, 0, 0.05)', borderColor: 'rgba(255, 152, 0, 0.4)', padding: '0px 12px' }}>
                          <Typography variant="body2" style={{ color: '#ccff00', fontSize: '0.85rem', lineHeight: 1.3, fontWeight: 'bold', textShadow: "0px 0px 8px rgba(204,255,0,0.8)" }}>
                            {box.referenceImage 
                              ? i18n.t("statusScheduler.aiDescribeReference") 
                              : i18n.t("statusScheduler.aiDescribeImage")}
                          </Typography>
                        </Alert>
                      </Box>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        {box.referenceImage && (
                          <Box 
                            position="relative" 
                            display="inline-flex" 
                            alignItems="flex-start"
                            style={{ cursor: 'default' }}
                          >
                            <Box
                              style={{
                                width: 120,
                                height: 120,
                                borderRadius: 10,
                                overflow: 'hidden',
                                border: '2px solid #e0e0e0',
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}
                            >
                              <img 
                                src={typeof box.referenceImage === 'string' 
                                  ? getFileUrl(box.referenceImage) 
                                  : URL.createObjectURL(box.referenceImage)} 
                                alt="referencia" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <IconButton
                                size="small"
                                style={{ position: 'absolute', bottom: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setZoomImg(typeof box.referenceImage === 'string' ? getFileUrl(box.referenceImage) : URL.createObjectURL(box.referenceImage)); 
                                }}
                              >
                                <ZoomInIcon />
                              </IconButton>
                            </Box>
                            {/* Botón X para quitar */}
                            <IconButton 
                              size="small" 
                              onClick={() => updateBox(box.id, { referenceImage: null })} 
                              disabled={box.status === "scheduled" || box.status === "generating"} 
                              style={{ 
                                position: 'absolute', 
                                top: -8, 
                                right: -8, 
                                padding: 2, 
                                backgroundColor: '#333', 
                                color: '#fff',
                                width: 22,
                                height: 22,
                              }}
                            >
                              <CloseIcon style={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                        {!box.referenceImage && (
                          <Box 
                            onClick={() => {
                              if (box.status !== "scheduled" && box.status !== "generating") {
                                setSelectorBoxId(box.id)
                              }
                            }}
                            style={{
                              width: 100,
                              height: 100,
                              borderRadius: 10,
                              border: '2px dashed rgba(128,128,128,0.5)',
                              backgroundColor: 'rgba(128,128,128,0.05)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: (box.status === "scheduled" || box.status === "generating") ? 'default' : 'pointer',
                              opacity: (box.status === "scheduled" || box.status === "generating") ? 0.5 : 1,
                              padding: 4
                            }}
                          >
                            <Typography variant="caption" align="center" style={{ fontSize: 12, lineHeight: 1.2 }} color="textSecondary">
                              {i18n.t("statusScheduler.aiSampleImage")}
                            </Typography>
                          </Box>
                        )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Columna Textos: Copy */}
                    <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <TextField
                        label={i18n.t("statusScheduler.aiCopy")}
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={4}
                      fullWidth
                      disabled={box.status === "scheduled"}
                      value={box.caption}
                      onChange={e => updateBox(box.id, { caption: e.target.value })}
                      inputProps={{ style: { paddingRight: '12px', textAlign: 'justify' } }}
                    />
                    </Grid>
                    
                    {/* Fila 2: Configuraciones */}
                    <Grid item xs={12} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: 8, paddingBottom: 0 }}>
                    {box.status !== "scheduled" && (
                      <Box mt={0.5}>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 2 }}>
                          {i18n.t("statusScheduler.aiFormatsToGenerate")}
                        </Typography>
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                          {["1:1", "9:16", "4:5"].map(ratio => (
                            <FormControlLabel
                              key={ratio}
                              control={
                                <Checkbox
                                  checked={!!box.aspectRatios[ratio]}
                                  onChange={(e) => {
                                    updateBox(box.id, {
                                      aspectRatios: { ...box.aspectRatios, [ratio]: e.target.checked }
                                    });
                                  }}
                                  color="primary"
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">{ratio === "1:1" ? i18n.t("statusScheduler.aiSquare") : ratio === "9:16" ? i18n.t("statusScheduler.aiStory") : i18n.t("statusScheduler.aiVertical")}</Typography>}
                            />
                          ))}
                          
                          {Object.values(box.aspectRatios).filter(Boolean).length > 1 && (
                            <Box ml={2} minWidth={250}>
                              <Alert severity="info" variant="outlined" style={{ padding: '0px 12px', borderRadius: 8, backgroundColor: 'rgba(3, 169, 244, 0.05)', borderColor: 'rgba(3, 169, 244, 0.4)' }}>
                                <Typography variant="caption" style={{ color: '#03a9f4' }}>
                                  <strong>Aviso:</strong> Múltiples formatos (x{Object.values(box.aspectRatios).filter(Boolean).length}) genera mayor consumo IA.
                                </Typography>
                              </Alert>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                    </Grid>

                      {/* Fila 3: Vista Previa de Imagen y Botón Generar */}
                      <Grid item xs={12} md={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 4, paddingBottom: 0 }}>
                        <div className={classes.imageCol} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 16, overflowX: 'auto', paddingTop: 0, paddingBottom: 0, width: '100%', minHeight: 200, backgroundColor: 'transparent', justifyContent: 'flex-start', alignItems: 'center' }}>
                          {box.status === "empty" && (
                            <Box 
                              display="flex" 
                              flexDirection="column" 
                              alignItems="center" 
                              justifyContent="center"
                              gap={2} 
                              margin="auto"
                              style={{
                                width: '100%',
                                minHeight: 250,
                                border: '2px dashed rgba(128, 128, 128, 0.4)',
                                borderRadius: 12,
                                backgroundColor: 'rgba(128, 128, 128, 0.05)',
                                padding: 16
                              }}
                            >
                              <Typography color="textSecondary">{i18n.t("statusScheduler.aiNotGenerated")}</Typography>
                              <Button
                                variant="contained"
                                className={classes.applyButton}
                                onClick={() => handleGenerate(box.id)}
                                startIcon={<PlayArrowIcon />}
                              >
                                {i18n.t("statusScheduler.aiGenerateButton")}
                              </Button>
                            </Box>
                          )}
                    {box.status === "generating" && (
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" margin="auto">
                        <CircularProgress />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                          Generando con Inteligencia Artificial...
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Esto puede tardar unos segundos
                        </Typography>
                      </Box>
                    )}
                    {(box.status === "generated" || box.status === "scheduled") && box.generatedImages && box.generatedImages.length > 0 && (
                      box.generatedImages.map((imgObj, idx) => (
                        <Box 
                          key={idx} 
                          style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                          onClick={() => updateBox(box.id, { selectedImageIndex: idx })}
                        >
                          <Box 
                            position="relative"
                            style={{
                              border: box.selectedImageIndex === idx ? '2px solid #00e676' : '1px solid rgba(76, 175, 80, 0.3)',
                              boxShadow: box.selectedImageIndex === idx ? '0 0 8px rgba(0, 230, 118, 0.4)' : 'none',
                              borderRadius: 12,
                              padding: 4,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: box.selectedImageIndex === idx ? 'rgba(0, 230, 118, 0.05)' : 'transparent',
                              transition: 'all 0.3s ease',
                              overflow: 'hidden'
                            }}
                          >
                            {box.selectedImageIndex === idx && (
                              <div style={{ position: 'absolute', top: 8, left: 8, backgroundColor: '#00e676', color: '#000', padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 'bold', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                Seleccionado
                              </div>
                            )}
                            {imgObj.imageUrl.match(/\.(mp4|webm|mov)$/i) ? (
                              <video 
                                src={imgObj.imageUrl} 
                                style={{ 
                                  height: 180, 
                                  width: 'auto', 
                                  maxWidth: 180, 
                                  objectFit: 'cover', 
                                  borderRadius: 8 
                                }} 
                                autoPlay
                                loop
                                muted
                                playsInline
                              />
                            ) : (
                              <img 
                                src={imgObj.imageUrl} 
                                style={{ 
                                  height: 180, 
                                  width: 'auto', 
                                  maxWidth: 180, 
                                  objectFit: 'contain', 
                                  borderRadius: 8 
                                }} 
                                alt={`Preview ${imgObj.aspectRatio}`} 
                              />
                            )}
                            <div className={classes.overlay} onClick={() => updateBox(box.id, { selectedImageIndex: idx })}>
                              <IconButton 
                                style={{ color: "#fff", backgroundColor: 'rgba(0,0,0,0.6)' }}
                                onClick={(e) => { e.stopPropagation(); setZoomImg(imgObj.imageUrl); }}
                              >
                                <ZoomInIcon />
                              </IconButton>
                            </div>
                          </Box>
                          
                          <Box 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center" 
                            mt={1.5} 
                            style={{ 
                              width: '100%', 
                              padding: '4px 12px',
                              backgroundColor: 'rgba(0, 229, 255, 0.05)',
                              border: '1px solid rgba(0, 229, 255, 0.4)',
                              boxShadow: '0 0 8px rgba(0, 229, 255, 0.2)',
                              borderRadius: 8
                            }}
                          >
                            <Typography variant="caption" style={{ fontWeight: 600, color: '#00e5ff' }}>{imgObj.aspectRatio}</Typography>
                            <IconButton 
                              size="small" 
                              style={{ color: '#00e5ff' }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await fetch(imgObj.imageUrl);
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.download = `generado_${box.id}_${imgObj.aspectRatio}.png`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error("Error downloading image:", error);
                                  toast.error("Error al descargar la imagen");
                                }
                              }}
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))
                    )}
                    

                  </div>
                </Grid>
              </Grid>
            </CardContent>

                <CardActions style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: "16px", borderTop: "1px solid rgba(128,128,128,0.2)" }}>
                  
                  {/* Fila 1: Selector de Comunidades (Ocupa todo el ancho si existe) */}
                  {box.status === "generated" && type !== "status" && (
                    <Box display="flex" alignItems="flex-start" width="100%">
                      <Autocomplete
                        multiple
                        size="small"
                        style={{ flexGrow: 1 }}
                        noOptionsText={"Sin resultados"}
                        options={Object.keys(restrictedGroups).length === 0
                          ? availableGroups
                          : availableGroups.filter((group) => type === "community" ? restrictedGroups[group.id]?.isCommunity : !restrictedGroups[group.id]?.isCommunity)}
                        getOptionLabel={(option) => option.name}
                        getOptionDisabled={(option) => restrictedGroups[option.id]?.isRestricted}
                        value={availableGroups.filter(g => box.groupIds.includes(`${g.number}@g.us`))}
                        onChange={(e, newValue) => {
                          updateBox(box.id, { groupIds: newValue.map(v => `${v.number}@g.us`) });
                        }}
                        disableCloseOnSelect
                        renderOption={(option, { selected }) => (
                          <React.Fragment>
                            <Checkbox
                              color="primary"
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.name} {restrictedGroups[option.id]?.isRestricted && <span style={{ marginLeft: 6 }} title="Bot no tiene permisos">🔒</span>}
                          </React.Fragment>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" label={type === "community" ? i18n.t("mainDrawer.listItems.publishCommunities") : i18n.t("statusScheduler.targetGroups")} placeholder="Buscar..." />
                        )}
                      />
                      <Tooltip title={type === "community" ? "Sincronizar Comunidades" : "Sincronizar grupos"}>
                        <Box ml={1} mt={0.5}>
                          <IconButton 
                            color="primary" 
                            onClick={onSyncGroups} 
                            disabled={syncingGroups || !selectedWhatsapp || selectedWhatsapp === "all"}
                            style={{ border: '1px solid rgba(0,0,0,0.1)', padding: 8 }}
                          >
                            {syncingGroups ? <CircularProgress size={24} /> : <SyncIcon />}
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Fila 2: Botones Izquierda y Controles Derecha */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" flexWrap="wrap" style={{ gap: 16 }}>
                    
                    {/* Izquierda: Borrador y Regenerar */}
                    <Box display="flex" style={{ gap: 16 }}>
                      <Button
                        variant="outlined"
                        className={classes.secondaryButton}
                        onClick={() => handleSaveDraft(box.id)}
                        startIcon={<SaveIcon />}
                      >
                        {i18n.t("statusScheduler.aiSaveDraft")}
                      </Button>

                      {box.status === "generated" && (
                        <Button
                          variant="outlined"
                          className={classes.secondaryButton}
                          onClick={() => handleGenerate(box.id)}
                          startIcon={<RefreshIcon />}
                        >
                          {i18n.t("statusScheduler.aiRegenerate") || "Regenerar"}
                        </Button>
                      )}
                    </Box>

                    {/* Derecha: Fecha y Aprobar */}
                    {box.status === "generated" && (
                      <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: '16px' }}>
                        <TextField
                          type="datetime-local"
                          size="small"
                          variant="outlined"
                          label={i18n.t("statusScheduler.time")}
                          InputLabelProps={{ shrink: true }}
                          value={box.scheduledAt}
                          style={{ minWidth: 200 }}
                          onChange={e => updateBox(box.id, { scheduledAt: e.target.value })}
                        />
                        <Button
                          variant="contained"
                          className={classes.applyButton}
                          style={{ whiteSpace: "nowrap", height: 40 }}
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleSchedule(box.id)}
                        >
                          Aprobar y Programar
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardActions>
              </Card>
            ))}
            </Box>
          </Box>
        </Box>
        </DialogContent>
      </Dialog>

      {zoomImg && (
        <Dialog open={true} onClose={() => setZoomImg(null)} maxWidth="lg">
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{i18n.t("statusScheduler.aiPreview")}</Typography>
              <IconButton onClick={() => setZoomImg(null)} size="small"><CloseIcon/></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {zoomImg.match(/\.(mp4|webm|mov)$/i) ? (
              <video src={zoomImg} controls autoPlay style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }} />
            ) : (
              <img src={zoomImg} alt="zoom" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }} />
            )}
          </DialogContent>
        </Dialog>
      )}

        <FileManagerSelectorModal
          open={!!selectorBoxId}
          onClose={() => setSelectorBoxId(null)}
          onSelect={(path) => updateBox(selectorBoxId, { referenceImage: path })}
          filterType="image"
        />

        {/* Dialog for AI Cost Confirmation */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} maxWidth="xs" fullWidth PaperProps={{ className: classes.dialogPaper }}>
          <DialogTitle>Autorizar Consumo IA</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {confirmDialog.isAll 
                ? `Estás a punto de generar imágenes para ${confirmDialog.count} publicaciones.` 
                : (confirmDialog.count > 1 ? `Estás a punto de generar ${confirmDialog.count} imágenes con IA.` : `Estás a punto de generar una imagen con IA.`)}
            </Typography>
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Modelo seleccionado: <b>{modelName}</b>
            </Typography>
            <Typography variant="body1" style={{ marginTop: 8 }}>
              Costo estimado: <b>{confirmDialog.cost.toFixed(4)} USD</b>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              ¿Deseas continuar y generar el contenido?
            </Typography>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} className={classes.secondaryButton}>
              Cancelar
            </Button>
            <Button onClick={confirmAndExecute} className={classes.applyButton}>
              Generar y Autorizar
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );
};

// Polyfill for AutoAwesomeIcon if not in mui v4
function AutoAwesomeIcon(props) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19 4.34l-1.5 4.5-4.5 1.5 4.5 1.5 1.5 4.5 1.5-4.5 4.5-1.5-4.5-1.5-1.5-4.5zm-8.5 2.16L8 2l-2.5 4.5L1 9l4.5 2.5L8 16l2.5-4.5L15 9l-4.5-2.5zm0 9l-1.5 2.5L5 20l3.5 1.5L10 24l1.5-2.5L15 20l-3.5-1.5L10.5 15.5z"/>
    </svg>
  );
}

export default AiContentFactory;
